import { getServiceUrlOrigin } from "@/api/servicePaths";
import { API_URL, APP_NAME, SIGNUP_EMAIL_VERIFICATION_ENABLED, SPTENANTID, WEBUI_URL } from "@/configs";
import { withAcceptLanguageHeader } from "@/lib/auth/preferredLocale";
import { logger } from "@/lib/logger";
import { isPublicAuthPath, shouldBypassSessionInterception } from "@/utils/authRoutes";
import { isEmailVerificationApiPath, isEmailVerificationPath } from "@/utils/authRoutes";
import { getCookie } from "@/utils/session";
import Router from "next/router";
import EmailPasswordReact from "supertokens-auth-react/recipe/emailpassword";
import EmailVerification from "supertokens-auth-react/recipe/emailverification";
import Passwordless from "supertokens-auth-react/recipe/passwordless";
import Session from "supertokens-auth-react/recipe/session";
import ThirdPartyReact from "supertokens-auth-react/recipe/thirdparty";
import { ThirdPartyPreBuiltUI } from "supertokens-auth-react/recipe/thirdparty/prebuiltui";

function stripQueryFromUrl(url: string | undefined): string | undefined {
  if (!url || typeof url !== "string") return url;

  try {
    const parsed = new URL(url, typeof window !== "undefined" ? window.location.origin : "http://localhost");
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url.split("?")[0];
  }
}

function normalizeHeaderEntries(headers: HeadersInit | undefined) {
  if (!headers) return [];

  if (headers instanceof Headers) {
    return Array.from(headers.entries());
  }

  if (Array.isArray(headers)) {
    return headers;
  }

  return Object.entries(headers);
}

function withTenantHeader(requestInit: RequestInit | undefined, tenantId: string | undefined) {
  const routeLocale = Router.locale && Router.locale !== Router.defaultLocale ? Router.locale : undefined;
  const headers = withAcceptLanguageHeader(requestInit?.headers, routeLocale);
  if (tenantId) {
    headers.set("sp-tenant-id", tenantId);
  }

  return {
    ...requestInit,
    headers
  };
}

function describeSafeHeaders(headers: HeadersInit | undefined, credentials: RequestCredentials | undefined) {
  const entries = normalizeHeaderEntries(headers);
  const lowerMap = new Map(entries.map(([key, value]) => [key.toLowerCase(), String(value)]));

  return {
    headerNames: entries.map(([key]) => key).sort(),
    hasAuthorization: lowerMap.has("authorization"),
    hasCookie: lowerMap.has("cookie"),
    hasRid: lowerMap.has("rid"),
    hasSpTenantId: lowerMap.has("sp-tenant-id"),
    stAuthMode: lowerMap.get("st-auth-mode") || null,
    credentials: credentials || null
  };
}

function getCompactCallStack() {
  try {
    return new Error().stack
      ?.split("\n")
      .slice(1, 7)
      .map((line) => line.trim());
  } catch {
    return undefined;
  }
}

function shouldSkipPublicAuthSessionProbe() {
  if (typeof window === "undefined") {
    return false;
  }

  return isPublicAuthPath(window.location.pathname) && !isEmailVerificationPath(window.location.pathname);
}

export const SuperTokensConfig = () => {
  // Enhanced validation for critical config
  const isMissingURL = !API_URL || !WEBUI_URL;
  const isInvalidURL = String(API_URL) === "undefined" || String(WEBUI_URL) === "undefined";

  if (typeof window !== "undefined" && (isMissingURL || isInvalidURL)) {
    logger.error("ST_LOGS", "Missing or invalid configuration on client side", {
      API_URL,
      WEBUI_URL,
      typeAPI: typeof API_URL,
      typeWEBUI: typeof WEBUI_URL
    });
  }

  if (isMissingURL || isInvalidURL) {
    const errorMsg = `Missing configuration for SuperTokens. API_URL: ${API_URL}, WEBUI_URL: ${WEBUI_URL}`;
    logger.error(errorMsg);
    throw new Error(errorMsg);
  }
  return {
    appInfo: {
      appName: APP_NAME || "Worksine",
      apiDomain: (getServiceUrlOrigin(API_URL) || API_URL) as string,
      websiteDomain: WEBUI_URL as string,
      apiBasePath: "/be/v1/auth",
      websiteBasePath: "/auth"
    },
    recipeList: [
      EmailPasswordReact.init({
        preAPIHook: async (context) => {
          const url = context.url;
          const tenantId = getCookie(SPTENANTID);
          const requestInit = withTenantHeader(context.requestInit, tenantId);
          return {
            url,
            requestInit
          };
        }
      }),
      ThirdPartyReact.init({
        preAPIHook: async (context) => {
          const url = context.url;
          const tenantId = getCookie(SPTENANTID);
          const requestInit = withTenantHeader(context.requestInit, tenantId);

          const action = context.action;
          if (action === "GET_AUTHORISATION_URL") {
            // This is where you can add custom headers to the request
          } else if (action === "THIRD_PARTY_SIGN_IN_UP") {
            // Note: this could either be sign in or sign up.
            // we don't know that at the time of the API call
            // since all we have is the authorisation code from
            // the social provider
          }

          // events such as sign out are in the
          // session recipe pre API hook (See the info box below)
          return {
            requestInit,
            url
          };
        },
        signInAndUpFeature: {
          providers: [ThirdPartyReact.Google.init()]
        }
      }),
      ...(SIGNUP_EMAIL_VERIFICATION_ENABLED
        ? [
            EmailVerification.init({
              mode: "REQUIRED",
              disableDefaultUI: true,
              preAPIHook: async (context) => {
                const url = context.url;
                const tenantId = getCookie(SPTENANTID);
                const requestInit = withTenantHeader(context.requestInit, tenantId);

                return {
                  url,
                  requestInit
                };
              },
              getRedirectionURL: async (context) => {
                if (context.action === "VERIFY_EMAIL") {
                  return "/auth/verify-email";
                }
                return undefined;
              }
            })
          ]
        : []),
      Session.init({
        tokenTransferMethod: "header", // or "cookie"
        override: {
          functions: (implementation) => {
            const implementationWithRefresh = implementation as typeof implementation & {
              attemptRefreshingSession?: (...args: any[]) => Promise<boolean>;
            };

            return {
              ...implementationWithRefresh,
              addAxiosInterceptors(input) {
                return implementation.addAxiosInterceptors(input);
              },
              addFetchInterceptorsAndReturnModifiedFetch(input) {
                return implementation.addFetchInterceptorsAndReturnModifiedFetch(input);
              },
              async doesSessionExist(input) {
                if (shouldSkipPublicAuthSessionProbe()) {
                  return false;
                }
                return implementation.doesSessionExist(input);
              },
              getAccessTokenPayloadSecurely(input) {
                return implementation.getAccessTokenPayloadSecurely(input);
              },
              getUserId(input) {
                return implementation.getUserId(input);
              },
              shouldDoInterceptionBasedOnUrl(toCheckUrl, apiDomain, sessionTokenBackendDomain) {
                const pathname = typeof window !== "undefined" ? window.location.pathname : undefined;
                if (shouldBypassSessionInterception(toCheckUrl, pathname)) {
                  logger.debug("ST_LOGS", {
                    "SESSION SHOULD_DO_INTERCEPTION": "BYPASSED_PUBLIC_AUTH_API",
                    url: stripQueryFromUrl(toCheckUrl),
                    pathname
                  });
                  return false;
                }

                if (
                  isPublicAuthPath(pathname) &&
                  !isEmailVerificationPath(pathname) &&
                  !isEmailVerificationApiPath(toCheckUrl)
                ) {
                  logger.debug("ST_LOGS", {
                    "SESSION SHOULD_DO_INTERCEPTION": "BYPASSED_PUBLIC_AUTH_ROUTE",
                    url: stripQueryFromUrl(toCheckUrl),
                    pathname
                  });
                  return false;
                }

                return implementation.shouldDoInterceptionBasedOnUrl(toCheckUrl, apiDomain, sessionTokenBackendDomain);
              },
              async attemptRefreshingSession(...args) {
                if (shouldSkipPublicAuthSessionProbe()) {
                  logger.debug("ST_LOGS", {
                    "SESSION ATTEMPT_REFRESHING_SESSION": "SKIPPED_ON_PUBLIC_AUTH_ROUTE",
                    pathname: typeof window !== "undefined" ? window.location.pathname : undefined
                  });
                  return false;
                }
                return implementationWithRefresh.attemptRefreshingSession
                  ? implementationWithRefresh.attemptRefreshingSession(...args)
                  : false;
              },
              signOut(input) {
                return implementation.signOut(input);
              }
            } as any;
          }
        },
        preAPIHook: async (context) => {
          const tenantId = getCookie(SPTENANTID);
          let requestInit = withTenantHeader(context.requestInit, tenantId);
          const safeHeaders = describeSafeHeaders(requestInit?.headers, requestInit?.credentials);

          logger.debug("ST_LOGS", {
            "SESSION PRE_API_HOOKS": context.action,
            url: stripQueryFromUrl(context.url),
            method: requestInit?.method || "GET",
            request: safeHeaders,
            pathname: typeof window !== "undefined" ? window.location.pathname : undefined,
            callStack: context.action === "REFRESH_SESSION" ? getCompactCallStack() : undefined
          });
          if (context.action === "SIGN_OUT") {
            const headers = {
              ...requestInit.headers
            };
            requestInit = {
              ...requestInit,
              headers
            };
          }
          return {
            url: context.url,
            requestInit
          };
        },
        onHandleEvent: (ctx) => {
          if (ctx.action === "API_INVALID_CLAIM") {
            const mfaError = ctx.claimValidationErrors.find((e) => e.id === "2fa-completed");
            if (mfaError) {
              logger.info("MFA Required", "Access denied: User must complete 2FA to access this resource.");
              return;
            }
          }
          // logger.debug("ST_LOGS", { "SESSION ON_HANDLE_EVENT": ctx.action });
        }
      }),
      Passwordless.init({ contactMethod: "EMAIL_OR_PHONE" })
    ],
    windowHandler: (oI: any) => {
      return {
        ...oI,
        location: {
          ...oI.location,
          setHref: (href: string) => {
            Router.push(href);
          }
        }
      };
    }
  };
};
export const PreBuiltUIList = [ThirdPartyPreBuiltUI];
